import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { FiLogIn, FiUserPlus, FiMail } from "react-icons/fi";

export default function Auth({ setIsAuthenticated, checkAuth }) { // Добавляем пропсы
  const canvasRef = useRef(null);
  const animationFrameId = useRef(null);
  const [tab, setTab] = useState("login");
  const [form, setForm] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const verified = params.get("verified") === "1";
  const reset = params.get("reset") === "1";
  const [errors, setErrors] = useState({});

  // Фон с частицами
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    function setCanvasSize() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
    setCanvasSize();

    const particles = Array.from({ length: 150 }).map(() => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      dx: (Math.random() - 0.5) * 1.2,
      dy: (Math.random() - 0.5) * 1.2,
      size: Math.random() * 3 + 1,
      alpha: Math.random() * 0.5 + 0.1,
    }));

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 120) {
            ctx.strokeStyle = `rgba(100, 100, 255, ${0.2 - distance / 600})`;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      particles.forEach((p) => {
        p.x += p.dx;
        p.y += p.dy;

        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;

        ctx.fillStyle = `rgba(100, 150, 255, ${p.alpha})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      });

      animationFrameId.current = requestAnimationFrame(animate);
    }
    animate();

    window.addEventListener("resize", setCanvasSize);
    return () => {
      window.removeEventListener("resize", setCanvasSize);
      if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
    };
  }, []);

  useEffect(() => {
    if (verified) {
      setMessage("Почта успешно подтверждена!");
      setMessageType("info");
    } else if (reset) {
      setMessage("Пароль успешно изменён!");
      setMessageType("info");
    }
  }, [location.search]);

  useEffect(() => {
    setErrors({});
    if (!verified && !reset) {
      setMessage("");
      setMessageType("info");
    }
  }, [tab, location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setErrors({});

    let url = "/api/login";
    let payload = { email: form.email, password: form.password };

    if (tab === "register") {
      url = "/api/register";
      payload.confirmPassword = form.confirmPassword;
    } else if (tab === "forgot") {
      url = "/api/forgot-password";
      payload = { email: form.email };
    }

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      setMessage(data.message || "");
      setMessageType(data.type || "info");
      setErrors(data.errors || {});

      if (res.ok) {
        setForm({ email: "", password: "", confirmPassword: "" });
        
        // КРИТИЧЕСКОЕ ИЗМЕНЕНИЕ: Обновляем состояние аутентификации
        if (tab === "login" || tab === "register") {
          setIsAuthenticated(true);
          await checkAuth(); // Ждем завершения проверки авторизации
          
          // Перенаправляем только после обновления состояния
          if (data.data?.redirect) {
            navigate(data.data.redirect);
          } else {
            navigate("/dashboard"); // На всякий случай, если redirect не пришел
          }
        } else if (data.data?.redirect) {
          navigate(data.data.redirect);
        }
      }
    } catch (err) {
      setMessage("Network error: " + err.message);
      setMessageType("error");
    }
  };

  const handleGoogleAuth = () => {
    window.location.href = "/auth/google";
  };

  const inputClass =
    "w-full px-4 py-3 rounded-xl bg-white/5 border text-white focus:outline-none focus:border-blue-400";

  const getInputClass = (field) =>
    `${inputClass} ${errors[field] ? "border-red-500 bg-red-100/10" : "border-white/20"}`;

  return (
    <div className="relative min-h-screen bg-gray-900 text-white overflow-hidden">
      <canvas ref={canvasRef} className="absolute inset-0 z-0" />
      <div className="absolute inset-0 z-0 bg-gradient-to-br from-blue-900/40 via-purple-900/30 to-black/50"></div>

      <section className="relative z-10 flex items-center justify-center h-screen px-4">
        <motion.div
          className="bg-white/10 backdrop-blur-lg p-8 rounded-3xl shadow-xl border border-white/20 max-w-md w-full"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Табы */}
          <div className="flex justify-center mb-6 space-x-4">
            {["login", "register", "forgot"].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-4 py-2 rounded-xl transition ${
                  tab === t
                    ? "bg-blue-500 text-white"
                    : "bg-white/10 text-gray-300 hover:bg-white/20"
                }`}
              >
                {t === "login" && "Вход"}
                {t === "register" && "Регистрация"}
                {t === "forgot" && "Сброс"}
              </button>
            ))}
          </div>

          {/* Формы */}
          <AnimatePresence mode="wait">
            <motion.form
              key={tab}
              onSubmit={handleSubmit}
              className="space-y-5"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Email */}
              <div>
                <label className="block text-gray-300 mb-1">Email</label>
                <input
                  type="email"
                  className={getInputClass("email")}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </div>

              {/* Пароль */}
              {tab !== "forgot" && (
                <div>
                  <label className="block text-gray-300 mb-1">Пароль</label>
                  <input
                    type="password"
                    className={getInputClass("password")}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                  />
                </div>
              )}

              {/* Подтверждение пароля */}
              {tab === "register" && (
                <div>
                  <label className="block text-gray-300 mb-1">Подтверждение пароля</label>
                  <input
                    type="password"
                    className={getInputClass("confirmPassword")}
                    value={form.confirmPassword}
                    onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                    required
                  />
                </div>
              )}

              {/* Ссылка на сброс */}
              {tab === "login" && (
                <div className="text-right">
                  <button
                    type="button"
                    className="text-sm text-blue-400 hover:underline"
                    onClick={() => setTab("forgot")}
                  >
                    Забыли пароль?
                  </button>
                </div>
              )}

              {/* Кнопка отправки */}
              <motion.button
                type="submit"
                className="w-full py-3 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-purple-500 hover:to-pink-500 rounded-xl font-semibold shadow-lg transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                {tab === "login" && <FiLogIn />}
                {tab === "register" && <FiUserPlus />}
                {tab === "forgot" && <FiMail />}
                {tab === "login" && "Войти"}
                {tab === "register" && "Зарегистрироваться"}
                {tab === "forgot" && "Отправить ссылку"}
              </motion.button>
            </motion.form>
          </AnimatePresence>

          {/* Google auth */}
          {tab !== "forgot" && (
            <>
              <div className="my-6 flex items-center">
                <hr className="flex-grow border-white/20" />
                <span className="px-3 text-gray-400 text-sm">или</span>
                <hr className="flex-grow border-white/20" />
              </div>
              <motion.button
                onClick={handleGoogleAuth}
                className="w-full py-3 flex items-center justify-center space-x-3 bg-white text-gray-800 rounded-xl font-semibold shadow-lg hover:shadow-xl transition"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <img src="/google-icon.svg" alt="Google" className="w-6 h-6" />
                <span>Войти через Google</span>
              </motion.button>
            </>
          )}

          {/* Сообщение */}
          {message && (
            <p
              className={`mt-4 text-center text-sm ${
                messageType === "info" ? "text-green-400" : "text-red-500"
              }`}
            >
              {message}
            </p>
          )}
        </motion.div>
      </section>
    </div>
  );
}

